import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";

import useCurrency from "../../../../../hooks/useCurrency";

const AMAZON_DOMAINS = {
  us: "www.amazon.com",
  ca: "www.amazon.ca",
  mx: "www.amazon.com.mx",
  uk: "www.amazon.co.uk",
  de: "www.amazon.de",
};

function getAmazonUrl(asin, marketplace) {
  const domain =
    AMAZON_DOMAINS[(marketplace || "us").toLowerCase()] || "www.amazon.com";
  return `https://${domain}/dp/${asin}`;
}

const PRODUCT_COLUMNS = [
  { key: "units", label: "Units sold", format: "number", align: "right" },
  {
    key: "refund_units",
    label: "Refund units",
    format: "number",
    align: "right",
  },
  { key: "sales", label: "Sales", format: "currency", align: "right" },
  { key: "ads", label: "Ads", format: "currency_negative", align: "right" },
  {
    key: "refunds_pct",
    label: "% Refunds",
    format: "percent",
    align: "right",
  },
  {
    key: "refund_cost",
    label: "Refund cost",
    format: "currency_negative",
    align: "right",
  },
  {
    key: "amazon_fees",
    label: "Amazon fees",
    format: "currency_negative",
    align: "right",
  },
  {
    key: "cogs",
    label: "COGS",
    format: "currency_negative",
    align: "right",
  },
  {
    key: "net_profit",
    label: "Net profit",
    format: "currency",
    align: "right",
  },
  { key: "margin", label: "Margin", format: "percent", align: "right" },
  {
    key: "real_acos",
    label: "Real ACOS",
    format: "percent",
    align: "right",
  },
];

const RAW_FIELDS = [
  "orderedProductSales",
  "item_promotion_discount",
  "refunded_amount",
  "referral_fee",
  "fulfillment_fee",
  "cogs_cost",
  "product_cost",
  "brand_cost",
  "display_cost",
  "extra_fee",
  "units",
  "refunds",
  "refund_units",
  "indirect_expenses",
  "estimated_payout",
  "sellable_returns",
];

function formatValue(
  value,
  format,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) {
  if (value === undefined || value === null || value === "") {
    return format === "percent"
      ? "0.00%"
      : format === "number"
      ? "0"
      : `${getCurrencySymbol()}0.00`;
  }
  const num = Number(value);
  if (isNaN(num)) return value;

  switch (format) {
    case "currency":
      return (
        getCurrencySymbol() +
        Number(convertCurrency(num)).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    case "currency_negative":
      return (
        (num < 0 ? `-${getCurrencySymbol()}` : getCurrencySymbol()) +
        Number(convertCurrency(Math.abs(num))).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    case "number":
      return num.toLocaleString("en-US");
    case "percent":
      return num.toFixed(2) + "%";
    default:
      return num.toLocaleString("en-US");
  }
}

function filterDateKeys(keys, months) {
  const monthSet = new Set(months);
  return keys.filter((k) => {
    const parts = k.split("-");
    if (parts.length >= 2) {
      const monthKey = `${parts[0]}-${parts[1]}`;
      return monthSet.has(monthKey) || monthSet.has(k);
    }
    return false;
  });
}

function aggregateAsinData(asinData, months) {
  const totals = {};
  RAW_FIELDS.forEach((f) => {
    totals[f] = 0;
  });

  const dateKeys = filterDateKeys(Object.keys(asinData), months);
  for (const dateKey of dateKeys) {
    const dayData = asinData[dateKey];
    if (!dayData) continue;
    RAW_FIELDS.forEach((f) => {
      if (dayData[f] !== undefined && dayData[f] !== null) {
        totals[f] += Number(dayData[f]) || 0;
      }
    });
  }

  return totals;
}

function computeProductRow(totals) {
  const sales = totals.orderedProductSales || 0;
  const ads =
    (totals.product_cost || 0) +
    (totals.brand_cost || 0) +
    (totals.display_cost || 0);
  const refundCost = totals.refunded_amount || 0;
  const amazonFees =
    (totals.fulfillment_fee || 0) +
    (totals.referral_fee || 0) -
    (totals.extra_fee || 0);
  const cogs = totals.cogs_cost || 0;
  const promos = totals.item_promotion_discount || 0;

  const grossProfit = sales - promos - refundCost - amazonFees - ads;
  const netProfit = grossProfit - cogs;
  const margin = sales ? (netProfit / sales) * 100 : 0;
  const realAcos = sales ? (ads / sales) * 100 : 0;
  const refundUnits = totals.refund_units || 0;
  const unitsSold = totals.units || 0;
  const refundsPct = unitsSold ? (refundUnits / unitsSold) * 100 : 0;

  return {
    units: unitsSold,
    refund_units: refundUnits,
    sales,
    ads,
    refunds_pct: refundsPct,
    refund_cost: refundCost,
    amazon_fees: amazonFees,
    cogs,
    net_profit: netProfit,
    margin,
    real_acos: realAcos,
  };
}

function getValueColor(key, value) {
  if (key === "net_profit") {
    if (value > 0) return "#2e7d32";
    if (value < 0) return "#d32f2f";
  }
  if (key === "margin") {
    if (value > 0) return "#2e7d32";
    if (value < 0) return "#d32f2f";
  }
  return "inherit";
}

function ProductRow({ product, productItemsMap, isParent, marketplace }) {
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const [expanded, setExpanded] = useState(true);
  const item = productItemsMap[product.asin];
  const hasChildren = product.children && product.children.length > 0;

  const cellSx = {
    py: 1,
    px: 1.5,
    fontSize: "0.82rem",
    whiteSpace: "nowrap",
    borderBottom: "1px solid",
    borderColor: "divider",
  };

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { backgroundColor: isParent ? "#eef2ff" : "#f5f8ff" },
          backgroundColor: isParent ? "#f5f8ff" : "#fff",
        }}
      >
        <TableCell
          sx={{
            ...cellSx,
            position: "sticky",
            left: 0,
            backgroundColor: isParent ? "#f5f8ff" : "#fff",
            zIndex: 1,
            minWidth: 270,
            maxWidth: 360,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ mr: 0.5, p: 0, minWidth: 20, width: 20 }}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 20, minWidth: 20, mr: 0.5 }} />
            )}
            {item?.image && (
              <Box
                component="img"
                src={item.image}
                alt={product.asin}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: "contain",
                  mr: 1,
                  borderRadius: 0.5,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ overflow: "hidden", minWidth: 0 }}>
              <Typography
                variant="body2"
                component="a"
                href={getAmazonUrl(product.asin, marketplace)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: isParent ? 700 : 600,
                  fontSize: "0.8rem",
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {product.asin}
                {item?.sku && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontSize: "0.7rem",
                      color: "text.secondary",
                    }}
                  >
                    - SKU {item.sku}
                  </Typography>
                )}
              </Typography>
              {item?.product_name && (
                <Tooltip title={item.product_name} arrow>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", maxWidth: 270 }}
                  >
                    {item.product_name}
                  </Typography>
                </Tooltip>
              )}
              {(item?.cogs || item?.fba_stock) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.disabled",
                    fontSize: "0.68rem",
                    display: "block",
                  }}
                >
                  {item?.cogs != null &&
                    `${getCurrencySymbol()}${convertCurrency(
                      Number(item.cogs).toFixed(2)
                    )} · COGS: ${getCurrencySymbol()}${convertCurrency(
                      Number(item.cogs).toFixed(2)
                    )}`}
                  {item?.fba_stock != null && ` · FBA stock: ${item.fba_stock}`}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        {PRODUCT_COLUMNS.map((col) => (
          <TableCell
            key={col.key}
            sx={{
              ...cellSx,
              textAlign: "right",
              color: getValueColor(col.key, product.data[col.key]),
              fontWeight:
                col.key === "net_profit" || col.key === "margin" || isParent
                  ? 600
                  : 400,
            }}
          >
            {formatValue(
              col.key === "ads" ||
                col.key === "refund_cost" ||
                col.key === "amazon_fees" ||
                col.key === "cogs"
                ? -Math.abs(product.data[col.key])
                : product.data[col.key],
              col.format,
              convertCurrency,
              getCurrencySymbol
            )}
          </TableCell>
        ))}
      </TableRow>
      {hasChildren &&
        expanded &&
        product.children.map((child) => (
          <ChildProductRow
            key={child.asin}
            child={child}
            productItemsMap={productItemsMap}
            marketplace={marketplace}
          />
        ))}
    </>
  );
}

function ChildProductRow({ child, productItemsMap, marketplace }) {
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const item = productItemsMap[child.asin];

  const cellSx = {
    py: 0.5,
    px: 1.5,
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    borderBottom: "1px solid",
    borderColor: "divider",
    color: "text.secondary",
  };

  return (
    <TableRow sx={{ backgroundColor: "#fafafa" }}>
      <TableCell
        sx={{
          ...cellSx,
          position: "sticky",
          left: 0,
          backgroundColor: "#fafafa",
          zIndex: 1,
          minWidth: 270,
          maxWidth: 360,
          pl: 7,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {item?.image && (
            <Box
              component="img"
              src={item.image}
              alt={child.asin}
              sx={{
                width: 32,
                height: 32,
                objectFit: "contain",
                mr: 1,
                borderRadius: 0.5,
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ overflow: "hidden", minWidth: 0 }}>
            <Typography
              variant="body2"
              component="a"
              href={getAmazonUrl(child.asin, marketplace)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: "0.76rem",
                color: "primary.main",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {child.asin}
            </Typography>
            {item?.product_name && (
              <Tooltip title={item.product_name} arrow>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  noWrap
                  sx={{ display: "block", maxWidth: 230 }}
                >
                  {item.product_name}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </TableCell>
      {PRODUCT_COLUMNS.map((col) => (
        <TableCell
          key={col.key}
          sx={{
            ...cellSx,
            textAlign: "right",
            color:
              getValueColor(col.key, child.data[col.key]) !== "inherit"
                ? getValueColor(col.key, child.data[col.key])
                : "text.secondary",
          }}
        >
          {formatValue(
            col.key === "ads" ||
              col.key === "refund_cost" ||
              col.key === "amazon_fees" ||
              col.key === "cogs"
              ? -Math.abs(child.data[col.key])
              : child.data[col.key],
            col.format,
            convertCurrency,
            getCurrencySymbol
          )}
        </TableCell>
      ))}
    </TableRow>
  );
}

function PnLByProduct({
  yearlyData,
  months,
  productItemsMap,
  selectedYear,
  store,
  marketplace,
}) {
  const [sortKey, setSortKey] = useState("units");
  const [sortDir, setSortDir] = useState("desc");
  const [groupMode, setGroupMode] = useState("parent");

  const asinDataMap = useMemo(() => {
    if (!yearlyData || typeof yearlyData !== "object") return {};
    const map = {};
    for (const asin of Object.keys(yearlyData)) {
      if (asin === "0") continue;
      const asinData = yearlyData[asin];
      if (!asinData) continue;
      const totals = aggregateAsinData(asinData, months);
      const data = computeProductRow(totals);
      if (data.units === 0 && data.sales === 0) continue;
      map[asin] = data;
    }
    return map;
  }, [yearlyData, months]);

  const productRows = useMemo(() => {
    if (groupMode === "child") {
      return Object.keys(asinDataMap).map((asin) => ({
        asin,
        data: asinDataMap[asin],
        children: [],
      }));
    }

    const parentGroups = {};
    const standalone = [];

    for (const asin of Object.keys(asinDataMap)) {
      const item = productItemsMap[asin];
      const parentAsin = item?.parent_asin;

      if (parentAsin && parentAsin !== asin) {
        if (!parentGroups[parentAsin]) {
          parentGroups[parentAsin] = [];
        }
        parentGroups[parentAsin].push({
          asin,
          data: asinDataMap[asin],
        });
      } else {
        standalone.push(asin);
      }
    }

    const rows = [];

    for (const parentAsin of Object.keys(parentGroups)) {
      const children = parentGroups[parentAsin];

      if (standalone.includes(parentAsin)) {
        standalone.splice(standalone.indexOf(parentAsin), 1);
      }

      const parentData = {};
      PRODUCT_COLUMNS.forEach((col) => {
        parentData[col.key] = 0;
      });

      if (asinDataMap[parentAsin]) {
        Object.assign(parentData, asinDataMap[parentAsin]);
      } else {
        for (const child of children) {
          PRODUCT_COLUMNS.forEach((col) => {
            parentData[col.key] += child.data[col.key] || 0;
          });
        }
        parentData.margin = parentData.sales
          ? (parentData.net_profit / parentData.sales) * 100
          : 0;
        parentData.real_acos = parentData.sales
          ? (parentData.ads / parentData.sales) * 100
          : 0;
        parentData.refunds_pct = parentData.units
          ? (parentData.refund_units / parentData.units) * 100
          : 0;
      }

      rows.push({
        asin: parentAsin,
        data: parentData,
        children,
        isParent: true,
      });
    }

    for (const asin of standalone) {
      rows.push({
        asin,
        data: asinDataMap[asin],
        children: [],
        isParent: true,
      });
    }

    return rows;
  }, [asinDataMap, productItemsMap, groupMode]);

  const sortedRows = useMemo(() => {
    const sorted = [...productRows];
    sorted.sort((a, b) => {
      const aVal = a.data[sortKey] || 0;
      const bVal = b.data[sortKey] || 0;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [productRows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const exportCsv = useCallback(() => {
    if (sortedRows.length === 0) return;

    const escCsv = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const header = [
      "ASIN",
      "SKU",
      "Product Name",
      ...PRODUCT_COLUMNS.map((c) => c.label),
    ];
    const rows = [header.map(escCsv).join(",")];

    for (const product of sortedRows) {
      const item = productItemsMap[product.asin];
      const cells = [
        escCsv(product.asin),
        escCsv(item?.sku || ""),
        escCsv(item?.product_name || ""),
        ...PRODUCT_COLUMNS.map((col) => {
          const val = product.data[col.key];
          return Math.round((val || 0) * 100) / 100;
        }),
      ];
      rows.push(cells.join(","));
    }

    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PNL-ByProduct-${store}-${
      marketplace || "us"
    }-${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedRows, productItemsMap, store, marketplace, selectedYear]);

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    borderBottom: "2px solid",
    borderColor: "divider",
    textAlign: "right",
    py: 1,
    px: 1.5,
    minWidth: 100,
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <ToggleButtonGroup
          value={groupMode}
          exclusive
          onChange={(_, val) => val && setGroupMode(val)}
          size="small"
        >
          <ToggleButton value="parent" sx={{ textTransform: "none", px: 2 }}>
            Parent
          </ToggleButton>
          <ToggleButton value="child" sx={{ textTransform: "none", px: 2 }}>
            Child
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={exportCsv}
          sx={{ whiteSpace: "nowrap" }}
        >
          CSV
        </Button>
      </Box>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ overflowX: "auto" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headerCellSx,
                  textAlign: "left",
                  position: "sticky",
                  left: 0,
                  backgroundColor: "#fff",
                  zIndex: 3,
                  minWidth: 270,
                }}
              >
                Product
              </TableCell>
              {PRODUCT_COLUMNS.map((col) => (
                <TableCell key={col.key} sx={headerCellSx}>
                  <TableSortLabel
                    active={sortKey === col.key}
                    direction={sortKey === col.key ? sortDir : "desc"}
                    onClick={() => handleSort(col.key)}
                    sx={{ flexDirection: "row-reverse" }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((product) => (
              <ProductRow
                key={product.asin}
                product={product}
                productItemsMap={productItemsMap}
                isParent={!!product.isParent}
                marketplace={marketplace}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default PnLByProduct;
